import cx from 'classnames';
import PropTypes from 'prop-types';

import { BadgeOriginal } from 'src/components/BadgeOriginal';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { ImageFrame } from 'src/components/ImageFrame';
import { ImageOverlayChip } from 'src/components/ImageOverlayChip';
import { Loader } from 'src/components/Loader';
import { ScoreWithDetailsLink } from 'src/components/ScoreWithDetailsLink';
import { ASSET_TYPE } from 'src/data/asset';
import { isNoScoreTool } from 'src/data/generationTool';
import { hasImageOverlayTexts, IMAGE_OVERLAY_TEXT_FIELD, prepareImageSrc } from 'src/data/image';
import { CHARACTER_BULLET } from 'src/lib/string';

import styles from './styles.module.scss';

const propTypes = {
  className: PropTypes.string,
  generationTool: PropTypes.string.isRequired,
  imageUrl: PropTypes.string,
  imageBlob: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string.isRequired,
  score: PropTypes.number,
  overlayData: PropTypes.shape({}),
  updating: PropTypes.bool,
  isRecomposeOriginal: PropTypes.bool,
  multiPromptTool: PropTypes.string,
};
const defaultProps = {
  className: null,
  imageUrl: null,
  imageBlob: null,
  title: null,
  score: null,
  overlayData: null,
  updating: false,
  isRecomposeOriginal: false,
  multiPromptTool: null,
};

export const VariationImageContent = ({
  className,
  generationTool,
  imageUrl,
  imageBlob,
  title = '',
  description,
  score,
  overlayData,
  updating = false,
  isRecomposeOriginal = false,
  multiPromptTool,
}) => {
  return (
    <div className={cx(styles.root, className)}>
      <div className={styles.mainContent}>
        <div className={styles.header}>
          {title && (
            <CustomTypography
              className={styles.title}
              variant={TYPOGRAPHY_VARIANT.regular12}
            >
              {title}
            </CustomTypography>
          )}

          {title && isRecomposeOriginal && (
            <span className={styles.bullet}>{CHARACTER_BULLET}</span>
          )}

          {isRecomposeOriginal && <BadgeOriginal />}
        </div>

        <div className={styles.content}>
          <div className={styles.imageContainer}>
            <ImageFrame
              className={styles.image}
              src={prepareImageSrc({ url: imageUrl, pngBase64: imageBlob })}
            />

            {updating && <Loader className={styles.loader} />}
          </div>

          <div className={styles.textContainer}>
            <div className={styles.descriptionGroup}>
              <CustomTypography
                className={styles.description}
                variant={TYPOGRAPHY_VARIANT.paragraph14}
              >
                {description}
              </CustomTypography>
            </div>

            {hasImageOverlayTexts(overlayData) && (
              <div className={styles.overlayGroup}>
                <CustomTypography
                  className={styles.textLabel}
                  variant={TYPOGRAPHY_VARIANT.regular12}
                >
                  Overlay text
                </CustomTypography>

                <div className={styles.overlayChips}>
                  {Object.values(IMAGE_OVERLAY_TEXT_FIELD).map((field) => {
                    const data = overlayData[field];
                    if (!data?.text) {
                      return null;
                    }

                    return (
                      <ImageOverlayChip
                        className={styles.overlayChip}
                        key={field}
                        text={data.text}
                        score={data.score}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {score != null && !isNoScoreTool(generationTool) && (
        <ScoreWithDetailsLink
          className={styles.score}
          generationTool={generationTool}
          score={score}
          multiPromptTool={multiPromptTool}
          assetType={ASSET_TYPE.image}
        />
      )}
    </div>
  );
};
VariationImageContent.displayName = 'VariationImageContent';
VariationImageContent.propTypes = propTypes;
VariationImageContent.defaultProps = defaultProps;
